'use client'

import {
  getDistrictMeta,
  type DistrictSelectorValue,
} from '@/components/experiences/districts'
import { DistrictPicker } from '@/components/experiences/district-picker'
import { captureAnalyticsEvent } from '@/lib/analytics'
import { getExperienceVisitorFacts } from '@/lib/experience-visitor-facts'
import {
  destinationExperiences,
  getHomepageExperiencePath,
  type HomepageDestination,
} from '@/lib/homepage-experiences'
import { motion, useReducedMotion } from 'framer-motion'
import { ArrowRight, BellRing, Clock3, MapPin } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'

const LIVE_DISTRICTS = Object.keys(destinationExperiences) as HomepageDestination[]
const DEFAULT_DISTRICT: DistrictSelectorValue = 'Thanjavur'
function isListedDistrict(district: DistrictSelectorValue): district is HomepageDestination {
  return (LIVE_DISTRICTS as string[]).includes(district)
}

export function ExperiencesExplorer() {
  const [district, setDistrict] = useState<DistrictSelectorValue>(DEFAULT_DISTRICT)
  const prefersReducedMotion = useReducedMotion()

  const meta = getDistrictMeta(district)
  const experiences = isListedDistrict(district) ? destinationExperiences[district] : []

  const chooseDistrict = (next: DistrictSelectorValue) => {
    if (next === district) return
    setDistrict(next)
    captureAnalyticsEvent('destination_selected', {
      destination: next,
      selection_source: 'experiences_option_wheel',
    })
  }

  return (
    <section aria-label="Experiences by district" className="border-b border-border py-12 md:py-16">
      <div className="site-shell">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-foreground/42">
          Tamil Nadu · Choose a district
        </p>

        <DistrictPicker value={district} onChange={chooseDistrict} />

        <div>
          <p className="sr-only" aria-live="polite">Showing experiences in {district}</p>
          {meta.image && (
            <div
              key={`banner-${district}`}
              className="relative mt-8 aspect-[16/10] w-full overflow-hidden rounded-[1.5rem] border border-border bg-secondary sm:aspect-[21/9] lg:aspect-[1280/480]"
            >
              <Image
                src={meta.image}
                alt={`${district} — ${meta.tagline}`}
                fill
                loading="lazy"
                quality={75}
                className="object-cover"
                sizes="(min-width: 1280px) 80vw, 100vw"
              />
            </div>
          )}
          <div className="mt-8">
            {meta.status === 'coming-soon' ? (
              <motion.div
                key={`coming-soon-${district}`}
                initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="relative overflow-hidden rounded-[2rem] bg-primary p-7 text-primary-foreground md:p-12"
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_18%_20%,white_1.4px,transparent_1.4px),radial-gradient(circle_at_78%_28%,white_1px,transparent_1px),radial-gradient(circle_at_32%_82%,white_1px,transparent_1px),radial-gradient(circle_at_86%_76%,white_1.4px,transparent_1.4px)] [background-size:30px_30px]"
                />
                <div className="relative grid items-start gap-8 sm:grid-cols-[auto_1fr]">
                  <span className="grid size-20 shrink-0 place-items-center rounded-full border border-white/20 bg-white/10 md:size-24">
                    <span className="text-center">
                      <span className="block font-sans text-4xl font-bold leading-none md:text-5xl">ஊ</span>
                      <span className="mt-1 block text-[0.55rem] font-bold uppercase tracking-[0.24em] text-white/70">
                        Oorva
                      </span>
                    </span>
                  </span>
                  <div>
                    <p className="inline-flex items-center gap-2 text-[0.66rem] font-bold uppercase tracking-[0.2em] text-brand-highlight">
                      <BellRing className="size-3.5" />
                      Opening next
                    </p>
                    <p className="mt-3 font-sans text-3xl font-semibold tracking-[-0.03em] md:text-5xl">
                      {district} is coming soon.
                    </p>
                    <p className="mt-3 max-w-xl leading-7 text-white/68">
                      {meta.tagline} We open district by district — finding hosts, walking
                      routes and testing every experience before bookings go live.
                    </p>
                    <Link href="/host" className="site-button-primary mt-6 w-fit">
                      Notify me at launch
                      <ArrowRight className="size-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={district}
                initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="grid gap-5 md:grid-cols-2 xl:grid-cols-3"
              >
                {experiences.map((experience) => {
                  const name = experience.title.replace('\n', ' ')
                  const facts = getExperienceVisitorFacts(experience)

                  return (
                    <div key={experience.slug}>
                      <Link
                        href={getHomepageExperiencePath(experience.slug)}
                        data-analytics-event="experience_selected"
                        data-analytics-experience-slug={experience.slug}
                        data-analytics-experience-name={name}
                        data-analytics-destination={district}
                        data-analytics-theme={experience.theme}
                        data-analytics-selection-source="experiences_catalogue"
                        className="group block h-full overflow-hidden rounded-[1.5rem] border border-border bg-card transition duration-300 hover:-translate-y-1 hover:border-brand/35 hover:shadow-[0_20px_50px_rgba(23,53,45,0.08)]"
                      >
                        <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
                          <Image
                            src={experience.image}
                            alt={`${name} in ${experience.location}`}
                            fill
                            className="object-cover transition duration-500 group-hover:scale-[1.03]"
                            sizes="(min-width: 1280px) 33vw, (min-width: 768px) 50vw, 100vw"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                          <span className="absolute bottom-4 left-4 rounded-full border border-white/20 bg-black/40 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/75 backdrop-blur">
                            {facts.live ? 'Hosted locally' : 'Opening next · booking not yet open'}
                          </span>
                        </div>
                        <div className="p-5 md:p-6">
                          <h3 className="font-sans text-2xl leading-tight tracking-[-0.02em]">{name}</h3>
                          <p className="mt-2 line-clamp-3 leading-7 text-muted-foreground">
                            {experience.oneLine}
                          </p>
                          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 border-t border-border pt-3 text-xs text-foreground/48">
                            <span className="inline-flex items-center gap-1.5">
                              <MapPin className="h-3.5 w-3.5 text-brand" />
                              {experience.location}
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <Clock3 className="h-3.5 w-3.5 text-brand" />
                              {facts.duration}
                            </span>
                            <span>{facts.price}</span>
                            <span>{facts.groupSize}</span>
                          </div>
                          <p className="mt-3 text-sm font-semibold text-brand">
                            {facts.live ? 'View details and request a date' : 'Preview experience · opening next'}
                          </p>
                        </div>
                      </Link>
                    </div>
                  )
                })}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
